import { CircleCheck, RefreshCw, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useTRPC } from '../../hooks/useTRPC';
import { getErrorMessage } from '../../utils/errorHandling';
import { captureAndReportError } from '../../utils/errorReporting';
import { LoadingDots } from '../LoadingDots';
import { Button } from '../Button';
import { PortalCard } from '../portal';

interface ResubmitHubspotButtonProps {
  releaseId: string;
  dappName: string;
  whatsNew?: string;
  isProcessed: boolean;
  hasNft: boolean;
  hubspotTicketId?: string | null;
  isCollectionVerified?: boolean;
  submissionStatus?: string;
  onSuccess?: () => void;
}

export function ResubmitHubspotButton({
  releaseId,
  dappName: _dappName,
  whatsNew = '',
  isProcessed,
  hasNft,
  hubspotTicketId,
  isCollectionVerified,
  submissionStatus,
  onSuccess,
}: ResubmitHubspotButtonProps) {
  const trpc = useTRPC();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [shouldRemove, setShouldRemove] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Only show button if:
  // 1. Has NFT minted (release was successfully processed)
  // 2. No HubSpot ticket ID (submission failed or never attempted)
  // 3. Collection is verified (required before submission)
  // 4. Not already submitted (prevents duplicate tickets from failed ticket search)
  const shouldShow =
    isProcessed &&
    hasNft &&
    !hubspotTicketId &&
    isCollectionVerified === true &&
    submissionStatus === 'NotSubmitted';

  // Track visibility with local state to prevent instant disappearance on parent rerender
  useEffect(() => {
    if (shouldShow && !isVisible && !shouldRemove) {
      setIsVisible(true);
    }
  }, [shouldShow, isVisible, shouldRemove]);

  const handleResubmit = async () => {
    setIsSubmitting(true);

    try {
      toast.info('Resubmitting to store...', { duration: 3000 });

      const submitResult = await trpc.release.submitToStore.mutate({
        releaseId,
        whatsNew,
        criticalUpdate: false,
        isResubmission: true,
      });

      if (submitResult._tag === 'Right' && submitResult.right.success) {
        setIsSuccess(true);

        // Wait 2 seconds to show success state, then trigger exit animation
        setTimeout(() => {
          setShouldRemove(true);

          // Wait for the 200ms exit animation before refetching data
          setTimeout(() => {
            setIsVisible(false);
            onSuccess?.();
          }, 200);
        }, 2000);

        toast.success(submitResult.right.message, {
          description: submitResult.right.hubspotTicketId
            ? `Ticket ID: ${submitResult.right.hubspotTicketId}`
            : undefined,
          duration: 8000,
        });
      } else {
        toast.error('Failed to resubmit release to store');
      }
    } catch (error) {
      captureAndReportError('Failed to resubmit:', error);
      const errorMessage = getErrorMessage(error);

      if (errorMessage.includes('already been submitted')) {
        toast.error('Release has already been submitted', {
          description: 'Check the submission status above',
        });
      } else {
        toast.error('Failed to resubmit release to store', {
          description: errorMessage,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      inert={shouldRemove}
      className={`grid transition-[grid-template-rows,opacity] duration-200 ease-[cubic-bezier(0.2,0,0,1)] ${
        shouldRemove
          ? 'grid-rows-[0fr] opacity-0'
          : 'grid-rows-[1fr] opacity-100'
      }`}
    >
      <div className="min-h-0 overflow-hidden">
        <PortalCard className="flex flex-wrap items-center justify-between gap-4 border-amber-500/30 p-6">
          <div className="flex min-w-0 items-center gap-3">
            <RefreshCw className="size-5 shrink-0 text-amber-400" />
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-white">Resubmit Ticket</h3>
              <p className="mt-0.5 text-sm text-seeker-400">
                Your release NFT was minted but your submission has not made it
                to our queue.
              </p>
            </div>
          </div>
          <Button
            onClick={handleResubmit}
            variant={isSuccess ? 'outlineSuccess' : 'outlineWhite'}
            className="group w-full shrink-0 sm:w-auto sm:min-w-[140px]"
            disabled={isSubmitting || isSuccess}
          >
            <div
              key={
                isSuccess ? 'success' : isSubmitting ? 'submitting' : 'resubmit'
              }
              className="flex items-center gap-2 animate-in fade-in zoom-in-95 duration-150"
            >
              {isSuccess ? (
                <>
                  <CircleCheck className="size-4" />
                  <span>Submitted</span>
                </>
              ) : isSubmitting ? (
                <>
                  <LoadingDots className="text-lg" />
                  <span>Submitting…</span>
                </>
              ) : (
                <>
                  <Send className="size-4 transition-transform duration-150 group-hover:translate-x-1 group-hover:-rotate-15" />
                  Resubmit
                </>
              )}
            </div>
          </Button>
        </PortalCard>
      </div>
    </div>
  );
}
